import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { 
  Crown, Check, Loader2, BarChart3, Repeat, 
  Target, Infinity, CreditCard, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export function PremiumModal({ open, onClose }) {
  const { user, updateTier } = useAuth();
  const [subStatus, setSubStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);

  useEffect(() => {
    if (open) fetchStatus();
  }, [open]);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/api/subscription/status`, { withCredentials: true });
      setSubStatus(res.data);
    } catch {} 
    finally { setLoadingStatus(false); }
  };

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/subscription/create-checkout`, {
        origin_url: window.location.origin
      }, { withCredentials: true });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start upgrade');
      setLoading(false);
    }
  };

  const isPremium = user?.tier === 'premium';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-modal sm:max-w-lg" data-testid="premium-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-bold text-white flex items-center gap-3">
            <Crown className="h-7 w-7 text-amber-400" />
            {isPremium ? 'Premium Active' : 'Upgrade to Premium'}
          </DialogTitle>
          <DialogDescription className="text-[#A19DB0]">
            {isPremium ? 'You have full access to all features' : 'Unlock the full power of FutureVault'}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6 space-y-6">
          {/* Pricing */}
          {!isPremium && (
            <div className="text-center p-6 bg-gradient-to-br from-[#9333EA]/20 to-amber-500/10 rounded-md border border-[#9333EA]/30">
              <div className="font-mono text-5xl font-black text-white mb-1">
                $4.99
              </div>
              <div className="text-[#A19DB0]">per month</div>
            </div>
          )}

          {/* Features */}
          <div className="space-y-3">
            <FeatureRow icon={<Infinity className="h-4 w-4" />} text="Unlimited savings goals" free="3 max" premium="Unlimited" isPremium={isPremium} />
            <FeatureRow icon={<BarChart3 className="h-4 w-4" />} text="Advanced analytics & charts" free={false} premium={true} isPremium={isPremium} />
            <FeatureRow icon={<Repeat className="h-4 w-4" />} text="Recurring auto-deposits" free={false} premium={true} isPremium={isPremium} />
            <FeatureRow icon={<Target className="h-4 w-4" />} text="Manual deposits" free={true} premium={true} isPremium={isPremium} />
            <FeatureRow icon={<CreditCard className="h-4 w-4" />} text="Card payments via Stripe" free={true} premium={true} isPremium={isPremium} />
            <FeatureRow icon={<Zap className="h-4 w-4" />} text="Shareable goal links" free={true} premium={true} isPremium={isPremium} />
          </div>

          {/* Fee info */}
          <div className="p-3 bg-[#12121A] rounded-md border border-white/10">
            <p className="text-xs text-[#A19DB0]">
              A 2% service fee applies to all card deposits. This helps keep FutureVault running.
            </p>
          </div>

          {/* Status / CTA */}
          {isPremium ? (
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-md">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="h-5 w-5 text-amber-400" />
                <span className="font-heading font-bold text-white">Premium Active</span>
              </div>
              {subStatus?.subscription?.expires_at && (
                <p className="text-sm text-[#A19DB0]">
                  Renews: {new Date(subStatus.subscription.expires_at).toLocaleDateString()}
                </p>
              )}
            </div>
          ) : (
            <Button
              data-testid="upgrade-premium-button"
              onClick={handleUpgrade}
              disabled={loading}
              className="w-full h-14 bg-gradient-to-r from-[#9333EA] to-amber-500 hover:from-[#A855F7] hover:to-amber-400 text-white font-bold text-lg transition-all"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Crown className="h-5 w-5 mr-2" />
                  Upgrade for $4.99/mo
                </>
              )}
            </Button>
          )}

          {/* Goal usage */}
          {!loadingStatus && subStatus && !isPremium && (
            <div className="text-center text-sm text-[#A19DB0]">
              Using {subStatus.current_goals} of {subStatus.max_goals} free goals
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FeatureRow({ icon, text, free, premium, isPremium }) {
  const showCheck = isPremium ? premium : free;
  
  return (
    <div className="flex items-center justify-between p-3 bg-[#0A0A0F] rounded-md border border-white/5">
      <div className="flex items-center gap-3">
        <div className="text-[#9333EA]">{icon}</div>
        <span className="text-sm text-white">{text}</span>
      </div>
      <div className="flex items-center gap-4">
        {!isPremium && (
          <>
            <div className="text-center w-16">
              {free === true ? (
                <Check className="h-4 w-4 text-emerald-400 mx-auto" />
              ) : free === false ? (
                <span className="text-xs text-red-400">-</span>
              ) : (
                <span className="text-xs text-[#A19DB0]">{free}</span>
              )}
            </div>
            <div className="text-center w-16">
              {premium === true ? (
                <Check className="h-4 w-4 text-[#9333EA] mx-auto" />
              ) : (
                <span className="text-xs text-[#9333EA]">{premium}</span>
              )}
            </div>
          </>
        )}
        {isPremium && (
          <Check className="h-4 w-4 text-emerald-400" />
        )}
      </div>
    </div>
  );
}

// Hook to check subscription after payment redirect
export function useSubscriptionVerify() {
  const { updateTier, checkAuth } = useAuth();
  
  const verify = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/api/subscription/verify/${sessionId}`, { withCredentials: true });
      if (res.data.status === 'active') {
        updateTier('premium');
        await checkAuth();
        toast.success('Welcome to Premium! All features unlocked.');
        return true;
      }
    } catch {}
    return false;
  };

  return { verify };
}
